<template>
  <div class="page-header">
    <h2>Gestion des Véhicules</h2>
    <button @click="openCreateModal" class="btn btn-primary">+ Nouveau véhicule</button>
  </div>
  <!-- Messages d'info -->
  <InfoBox
    v-if="infoMessage"
    v-model:open="infoOpen"
    :type="infoType"
    @close="infoOpen = false; infoMessage = ''"
  >
    {{ infoMessage }}
  </InfoBox>
  <!-- Filtres -->
  <div class="filters form-group">
    <div class="btn-group">
      <div class="filters-group">
        <label class="field__label">Immatriculation</label>
        <input v-model="filters.immat" type="text" placeholder="Rechercher..." class="input" />
      </div>
      <div class="filters-group">
        <label class="field__label">Compagnie</label>
        <select v-model="filters.companyId" class="input">
          <option value="">-- Toutes les compagnies --</option>
          <option v-for="company in companies" :key="company.id" :value="company.id">
            {{ company.name }}
          </option>
        </select>
      </div>
      <div class="filters-group">
        <label class="field__label">Passagers min.</label>
        <input v-model.number="filters.minCapacity" type="number" placeholder="Ex: 30" class="input" />
      </div>
      <div class="filters-group">
        <label class="field__label">Passagers max.</label>
        <input v-model.number="filters.maxCapacity" type="number" placeholder="Ex: 80" class="input" />
      </div>
    </div>
    <div class="btn-group">
      <span class="badge badge-info">{{ filteredVehicles.length }} véhicule(s) trouvé(s) sur {{
        allVehicles.length }}</span>
      <button @click="resetFilters" class="btn btn-secondary">Réinitialiser</button>
    </div>
  </div>
  <!-- Cards -->
  <div v-if="!loading && filteredVehicles.length > 0" class="card-group">
    <div
      v-for="vehicle in filteredVehicles"
      :key="vehicle.id"
      class="card card-admin"
      :class="{ 'is-modified': isModified(vehicle.id) }"
    >
      <div class="card-content">
        <div class="form-group">
          <label>📷 Miniature</label>
          <div class="thumbnail-upload">
            <div v-if="getThumbnailUrl(vehicle.id)" class="thumbnail-preview">
              <img :src="getThumbnailUrl(vehicle.id)" :alt="`Miniature ${vehicle.noImmat}`" />
              <button
                @click="removeThumbnail(vehicle.id)"
                class="btn btn-danger-secondary btn-sm"
                type="button"
              >
                🗑️
              </button>
            </div>
            <input
              type="file"
              accept="image/*"
              @change="handleThumbnailChange(vehicle.id, $event)"
              :ref="`fileInput-${vehicle.id}`"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Immatriculation</label>
          <input
            v-model="editForms[vehicle.id].noImmat"
            type="text"
            class="mono"
            placeholder="Ex: AB-123-CD"
            @input="markAsModified(vehicle.id)"
          />
        </div>
        <div class="form-group">
          <label>🏢 Compagnie</label>
          <select
            v-model="editForms[vehicle.id].companyId"
            @change="markAsModified(vehicle.id)"
          >
            <option value="">-- Sélectionner une compagnie --</option>
            <option v-for="company in companies" :key="company.id" :value="company.id">
              {{ company.name }}
            </option>
          </select>
        </div>
        <div class="group-rows">
          <div class="form-group">
            <label>Passagers MAX</label>
            <input
              v-model.number="editForms[vehicle.id].nbPlaces"
              type="number"
              placeholder="Ex: 50"
              @input="markAsModified(vehicle.id)"
            />
          </div>

          <div class="form-group">
            <label>Bagages MAX</label>
            <input
              v-model.number="editForms[vehicle.id].nbBags"
              type="number"
              placeholder="Ex: 2"
              min="0"
              @input="markAsModified(vehicle.id)"
            />
          </div>
        </div>
        <div class="form-group">
          <label>Fonctionnalités</label>
          <div class="pills">
            <span
              class="pill"
              :class="editForms[vehicle.id].airConditioning ? 'pill--active' : 'pill--inactive'"
              @click="toggleFeature(vehicle.id, 'airConditioning')"
              role="button"
              tabindex="0"
            >
              ❄️ Climatisation
            </span>
            <span
              class="pill"
              :class="editForms[vehicle.id].toilet ? 'pill--active' : 'pill--inactive'"
              @click="toggleFeature(vehicle.id, 'toilet')"
              role="button"
              tabindex="0"
            >
              🚽 Toilettes
            </span>
            <span
              class="pill"
              :class="editForms[vehicle.id].tv ? 'pill--active' : 'pill--inactive'"
              @click="toggleFeature(vehicle.id, 'tv')"
              role="button"
              tabindex="0"
            >
              📺 TV
            </span>
            <span
              class="pill"
              :class="editForms[vehicle.id].refreshments ? 'pill--active' : 'pill--inactive'"
              @click="toggleFeature(vehicle.id, 'refreshments')"
              role="button"
              tabindex="0"
            >
              <!--☕ Rafraîchissements-->
              <!--🥤 Rafraîchissements-->
              🍽️ Restauration
            </span>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <button
          @click="saveVehicle(vehicle.id)"
          class="btn btn-success"
          :class="{ 'is-highlight': isModified(vehicle.id) }"
          :disabled="!isModified(vehicle.id)"
        >
          💾 Enregistrer
        </button>
        <button @click="openDeleteModal(vehicle)" class="btn btn-danger-secondary">
          🗑 Supprimer
        </button>
      </div>
    </div>
  </div>
  <!-- État vide -->
  <EmptyState
    v-if="!loading && filteredVehicles.length === 0 && allVehicles.length > 0"
    :is-empty="true"
    emptyMessage="Aucun véhicule ne correspond aux filtres"
  />
  <EmptyState
    v-if="!loading && allVehicles.length === 0"
    :is-empty="true"
    emptyMessage="Aucun véhicule pour le moment"
  />
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, onMounted, reactive, computed } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useModalStore } from '@/stores/modalStore'
import FormFieldsVehicle from '@/components/FormFields/FormFieldsVehicle.vue'
import InfoBox from '@/components/InfoBox.vue'
import EmptyState from '@/components/EmptyState.vue'

const emit = defineEmits(['loading'])

const toast = useToast()
const modalStore = useModalStore()

const allVehicles = ref([])
const companies = ref([])
const loading = ref(false)
const thumbnailFiles = reactive({})
const thumbnailPreviews = reactive({})

const editForms = reactive({})
const modifiedVehicles = ref(new Set())

const infoMessage = ref('')
const infoType = ref('info')
const infoOpen = ref(false)

const filters = ref({
  immat: '',
  companyId: '',
  minCapacity: null,
  maxCapacity: null
})

const initEditForm = (vehicle) => {
  editForms[vehicle.id] = {
    noImmat: vehicle.noImmat || '',
    nbPlaces: vehicle.nbPlaces || 0,
    companyId: vehicle.company?.id || '',
    airConditioning: vehicle.airConditioning ?? false,
    nbBags: vehicle.nbBags ?? 0,
    toilet: vehicle.toilet ?? false,
    tv: vehicle.tv ?? false,
    refreshments: vehicle.refreshments ?? false,
    thumbnailName: vehicle.thumbnailName || ''
  }
  thumbnailFiles[vehicle.id] = null
  thumbnailPreviews[vehicle.id] = null
}

const markAsModified = (vehicleId) => {
  modifiedVehicles.value.add(vehicleId)
}

const isModified = (vehicleId) => {
  return modifiedVehicles.value.has(vehicleId)
}

const toggleFeature = (vehicleId, feature) => {
  editForms[vehicleId][feature] = !editForms[vehicleId][feature]
  markAsModified(vehicleId)
}

const resetFilters = () => {
  filters.value = {
    immat: '',
    companyId: '',
    minCapacity: null,
    maxCapacity: null
  }
}

const handleThumbnailChange = (vehicleId, event) => {
  const file = event.target.files[0]

  if (!file) return

  if (!file.type.startsWith('image/')) {
    toast.error('Le fichier doit être une image')
    return
  }

  if (file.size > 5 * 1024 * 1024) {
    toast.error('L\'image ne doit pas dépasser 5 Mo')
    return
  }

  thumbnailFiles[vehicleId] = file

  const reader = new FileReader()
  reader.onload = (e) => {
    thumbnailPreviews[vehicleId] = e.target.result
  }
  reader.readAsDataURL(file)

  markAsModified(vehicleId)
}

// URL de la miniature (preview ou existante)
const getThumbnailUrl = (vehicleId) => {
  // Priorité à la preview si un nouveau fichier est sélectionné
  if (thumbnailPreviews[vehicleId]) {
    return thumbnailPreviews[vehicleId]
  }

  // Sinon, l'image existante
  const thumbnailName = editForms[vehicleId]?.thumbnailName
  if (thumbnailName) {
    return `${config.apiUrl}/uploads/thumbnails/${thumbnailName}`
  }

  return null
}

// Supprime la miniature
const removeThumbnail = (vehicleId) => {
  thumbnailFiles[vehicleId] = null
  thumbnailPreviews[vehicleId] = null
  editForms[vehicleId].thumbnailName = ''
  markAsModified(vehicleId)
}

const filteredVehicles = computed(() => {
  return allVehicles.value.filter(vehicle => {
    if (filters.value.immat) {
      const searchTerm = filters.value.immat.toLowerCase()
      const immat = vehicle.noImmat?.toLowerCase() || ''
      if (!immat.includes(searchTerm)) return false
    }

    if (filters.value.companyId) {
      if (vehicle.company?.id !== parseInt(filters.value.companyId)) return false
    }

    if (filters.value.minCapacity !== null && filters.value.minCapacity !== '') {
      if (vehicle.nbPlaces < filters.value.minCapacity) return false
    }

    if (filters.value.maxCapacity !== null && filters.value.maxCapacity !== '') {
      if (vehicle.nbPlaces > filters.value.maxCapacity) return false
    }

    return true
  })
})

onMounted(async () => {
  await Promise.all([loadVehicles(), loadCompanies()])
})

const loadVehicles = async () => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(config.apiUrl + '/vehicles', {
      credentials: 'include'
    })

    if (res.ok) {
      allVehicles.value = await res.json()
      allVehicles.value.forEach(vehicle => {
        initEditForm(vehicle)
      })
    } else {
      showInfo('error', 'Erreur lors du chargement des véhicules')
      toast.error('Erreur lors du chargement des véhicules')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const loadCompanies = async () => {
  try {
    const res = await fetch(config.apiUrl + '/companies', {
      credentials: 'include'
    })

    if (res.ok) {
      companies.value = await res.json()
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur lors du chargement des compagnies')
    console.error('Erreur lors du chargement des compagnies', err)
  }
}

const saveVehicle = async (vehicleId) => {
  const form = editForms[vehicleId]

  if (!form.noImmat || !form.nbPlaces || !form.companyId) {
    showInfo('error', 'Tous les champs sont requis')
    toast.error('Tous les champs sont requis')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    // 1️⃣ Sauvegarde les données du véhicule (sans le fichier)
    const res = await fetch(`${config.apiUrl}/vehicle/${vehicleId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        noImmat: form.noImmat,
        nbPlaces: form.nbPlaces,
        companyId: form.companyId,
        airConditioning: form.airConditioning,
        nbBags: form.nbBags,
        toilet: form.toilet,
        tv: form.tv,
        refreshments: form.refreshments
      })
    })

    if (!res.ok) {
      showInfo('error', 'Erreur lors de la modification')
      toast.error('Erreur lors de la modification')
      return
    }

    // 2️⃣ Upload du thumbnail si un nouveau fichier est sélectionné [web:21][web:24]
    if (thumbnailFiles[vehicleId]) {
      const formData = new FormData()
      formData.append('thumbnail', thumbnailFiles[vehicleId])

      const thumbnailRes = await fetch(`${config.apiUrl}/vehicle/${vehicleId}/thumbnail`, {
        method: 'POST',
        credentials: 'include',
        body: formData
        // ⚠️ Ne pas définir Content-Type, le navigateur le fait automatiquement avec boundary
      })

      if (!thumbnailRes.ok) {
        showInfo('warning', 'Véhicule modifié mais erreur lors de l\'upload de l\'image')
        toast.warning('Véhicule modifié mais erreur lors de l\'upload de l\'image')
      }
    }

    // 3️⃣ Suppression du thumbnail si demandé
    if (form.thumbnailName === '' && !thumbnailFiles[vehicleId]) {
      await fetch(`${config.apiUrl}/vehicle/${vehicleId}/thumbnail`, {
        method: 'DELETE',
        credentials: 'include'
      })
    }

    await loadVehicles()
    modifiedVehicles.value.delete(vehicleId)
    toast.success('Véhicule modifié avec succès')
    showInfo('success', 'Véhicule modifié avec succès')
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 Modale de création - CORRECTION
const openCreateModal = async () => {
  const { result } = modalStore.openComponent(  // ✅ Destructure { result }
    FormFieldsVehicle,
    {
      companies: companies.value
    },
    {
      title: 'Créer un véhicule',
      maxWidth: '600px',
      kind: 'default',
    }
  )

  const res = await result  // ✅ Attends la promise

  if (res.action === 'submit') {
    await handleCreateVehicle(res.data)
  }
}

const handleCreateVehicle = async (formData) => {
  if (!formData.noImmat || !formData.nbPlaces || !formData.companyId) {
    toast.error('Tous les champs sont requis')
    return
  }

  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(config.apiUrl + '/vehicle', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        noImmat: formData.noImmat,
        nbPlaces: formData.nbPlaces,
        companyId: formData.companyId,
        airConditioning: formData.airConditioning || false,
        nbBags: formData.nbBags || 0,
        toilet: formData.toilet || false,
        tv: formData.tv || false,
        refreshments: formData.refreshments || false,
        thumbnailName: formData.thumbnailName || ''
      })
    })

    if (res.ok) {
      await loadVehicles()
      toast.success('Véhicule créé avec succès')
      showInfo('success', 'Véhicule créé avec succès')
    } else {
      showInfo('error', 'Erreur lors de la création')
      toast.error('Erreur lors de la création')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

// 🎯 Modale de suppression - CORRECTION
const openDeleteModal = async (vehicle) => {
  const { result } = modalStore.open({  // ✅ Destructure { result }
    kind: 'confirm',
    title: 'Confirmer la suppression',
    message: `Êtes-vous sûr de vouloir supprimer ce véhicule ?

Immatriculation: ${vehicle.noImmat}
Capacité: ${vehicle.nbPlaces} places
Compagnie: ${vehicle.company?.name || 'N/A'}`,
    confirmText: 'Supprimer',
    cancelText: 'Annuler'
  })

  const res = await result  // ✅ Attends la promise

  if (res.action === 'confirm') {
    await handleDeleteVehicle(vehicle.id)
  }
}

const handleDeleteVehicle = async (vehicleId) => {
  loading.value = true
  emit('loading', true)

  try {
    const res = await fetch(`${config.apiUrl}/vehicle/${vehicleId}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (res.ok) {
      await loadVehicles()
      showInfo('success', 'Véhicule supprimé avec succès')
      toast.success('Véhicule supprimé avec succès')
    } else {
      showInfo('error', 'Erreur lors de la suppression')
      toast.error('Erreur lors de la suppression')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
    console.error(err)
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const showInfo = (type = 'info', message) => {
  infoMessage.value = message
  infoType.value = type
  infoOpen.value = true
}
</script>
<style scoped>
.card.card-admin {
  gap: 2rem;
}
.group-rows {
  flex-wrap: nowrap;
}



.card.card-admin {
  gap: 2rem;
}

.pill {
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.pill:hover {
  opacity: 0.8;
  transform: translateY(-1px);
}

.pill:active {
  transform: translateY(0);
}

.pill--active {
  background-color: var(--color-success, #22c55e);
  color: white;
  font-weight: 500;
}

.pill--inactive {
  background-color: var(--color-gray-200, #e5e7eb);
  color: var(--color-gray-600, #6b7280);
  opacity: 0.7;
}

.card.card-admin {
  gap: 2rem;
}

.pill {
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.pill:hover {
  opacity: 0.8;
  transform: translateY(-1px);
}

.pill:active {
  transform: translateY(0);
}

.pill--active {
  background-color: var(--color-success, #22c55e);
  color: white;
  font-weight: 500;
}

.pill--inactive {
  background-color: var(--color-gray-200, #e5e7eb);
  color: var(--color-gray-600, #6b7280);
  opacity: 0.7;
}
</style>
